import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { PrintOutlined } from '@mui/icons-material';
import { Box, Button, Container, Typography, TextField, MenuItem } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { callPostApi } from '../../function/ApiCallFunction';
import { useReactToPrint } from 'react-to-print';
import GradientLoader from '../../components/CommanComponents/GradientLoader';
import { QRCodeCanvas } from 'qrcode.react';
import { DataContext } from '../../context/DataContext';

/* ---------------- QR ITEM ---------------- */

const QRItem = memo(({ id, showText }) => (
  <div className="qr-item">
    {showText && (<div className="qr-text" style={{fontSize: '0.6rem',marginBottom: '2px',textAlign: 'center'}}>{id}</div>)}
    <QRCodeCanvas value={id} size={90} />
  </div>
));

/* ---------------- MAIN COMPONENT ---------------- */
const PrintPhysicalCardQr= () => {

    const qrRef    = useRef();
    const navigate = useNavigate();

    const {loginData } = useContext(DataContext);
    const store_id = loginData?.store_info?.store_id || 'STORE';
    const [searchParams, setSearchParams] = useSearchParams();
    const drawId = searchParams.get("draw_id") || "";

    const [loading,   setLoading]   = useState(false);
    const [datar,     setDatar]     = useState([]);
    const [cardCount, setCardCount] = useState("");
    const [qrIdPrint, setQrIdPrint] = useState(false);
    const [draws, setDraws] = useState([]);
    const [selected, setSelected] = useState(null);
    const drawCode = selected?.print_no ? String(Number(selected.print_no)) : "";
   
    /* ---------------- PRINT CONFIG ---------------- */
    const handlePrint = useReactToPrint({
        contentRef: qrRef,
        documentTitle: "QR Cards A3",
        pageStyle: `
          @page { size: A3 landscape; margin: 5mm; }
          body { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; background: white !important;}
          .MuiCard-root { page-break-inside: avoid; break-inside: avoid; }
        `,
    });
    
    /* ---------------- ID GENERATOR ---------------- */
    const generateId = (length = 6) => 
    {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";

        for (let i = 0; i < length; i++) { result += chars.charAt(Math.floor(Math.random() * chars.length)); }
        result = result.toLocaleUpperCase();
        return `${result}`;

    };

    const applyCoupons = () => {
        const count = Math.min(500, Number(String(cardCount).replace(/\D/g, "")) || 0);
        const printNo = String(Number(String(selected?.print_no || "").replace(/\D/g, "")) || "").padStart(2, "0");
        setLoading(true);
        if (!drawId || selected?.draw_id !== drawId || printNo === "00" || count <= 0) {
            setDatar([]);
            setLoading(false);
            return;
        }
        const list = [];
        for (let i = 1; i <= count; i++) {
            const uniqueId = `${store_id}${printNo}${generateId(6)}`;
            list.push({ id: uniqueId });
        }
        setDatar(list);
        setLoading(false);
    };

    const pickDraw = (value) => {
        setSearchParams(value ? { draw_id: value } : {}, { replace: true });
        setDatar([]);
    };

    useEffect(() => {
        if (!store_id || store_id === "STORE") return undefined;
        let mounted = true;
        callPostApi("store-draw", { mod: "DRAW_LIST", data_arr: { store_id } }).then((result) => {
            if (!mounted || result.status !== "200") return;
            setDraws(result.data?.draws || []);
        });
        return () => { mounted = false; };
    }, [store_id]);

    useEffect(() => {
        if (!store_id || !drawId) { setSelected(null); return undefined; }
        let mounted = true;
        callPostApi("store-draw", { mod: "DRAW_DETAIL", data_arr: { store_id, draw_id: drawId } }).then((result) => {
            if (!mounted) return;
            setSelected(result.status === "200" ? result.data?.draw || null : null);
        });
        return () => { mounted = false; };
    }, [store_id, drawId]);

    const clearFun = () => {
        setCardCount("");
        setQrIdPrint(false);
        setDatar([]);
        setSearchParams({}, { replace: true });
    };


  return (
    <Box>
      <Container>

          {/* BACK BUTTON */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button variant="contained" sx={{ backgroundColor: '#000', color: '#ffffff' }} onClick={() => navigate(-1)}> Back </Button>
          </Box>

          {/* TITLE */}
          <Typography variant="h6"  gutterBottom sx={{ background: 'linear-gradient(195deg, #49a3f1, #1A73E8)', p: 1, color: '#ffffff',
              borderRadius: '4px', mb: 3, display: 'flex', alignItems: 'center',}}> <PrintOutlined sx={{ mx: 1 }} /> Print Lucky Draw Coupons
          </Typography>
          
          {/* PRINT BUTTON */}
          <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
            <Button variant="contained" sx={{ background: 'linear-gradient(195deg, #42424a, #191919)', color: '#ffffff', ml:1}}
              onClick={handlePrint} > <PrintOutlined sx={{ mx: 1 }} /> Print </Button >      
          </Box>
       
          {/* FILTER */}
          <Box sx={{ display: 'flex',flexWrap:'wrap', gap: 2, mb: 2 }}>        
            <TextField select label="Lucky Draw Coupons" value={drawId} onChange={(e) => pickDraw(e.target.value)} sx={{ minWidth: 260 }}
              helperText={selected ? "Draw selected" : "1. Select the draw"}>
              <MenuItem value="">Select draw</MenuItem>
              {draws.map((draw) => (
                <MenuItem key={draw.draw_id} value={draw.draw_id}>{draw.print_no ? `${Number(draw.print_no)} · ` : ""}{draw.title}</MenuItem>
              ))}
            </TextField>
            <TextField label="Code" value={drawCode} sx={{ width: 110 }} inputProps={{ readOnly: true }} helperText="2. Draw code in the barcode" />
            <TextField label="Number of coupons" value={cardCount}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, "").slice(0, 3);
                setCardCount(Number(next) > 500 ? "500" : next);
                setDatar([]);
              }}
              sx={{ minWidth: 180 }} helperText="3. How many to print (max 500)" />
            <TextField select label="Show code" value={qrIdPrint} onChange={(e) => { setQrIdPrint(e.target.value);}} sx={{ minWidth: 140 }}>
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
            </TextField>
            <Button variant="contained" sx={{ height: 56, background: 'linear-gradient(195deg, #49a3f1, #1A73E8)' }}
              disabled={!drawId || !drawCode || !(Number(cardCount) > 0)} onClick={applyCoupons}>Apply</Button>
            <Button variant="outlined" color="error" sx={{ height: 56 }} onClick={clearFun}>Clear</Button>
          </Box>
          
          <Box ref={qrRef} className="print-grid" sx={{ display: "grid", gridTemplateColumns: "repeat(13, 1fr)",
              gridTemplateRows: "repeat(13, auto)", gap: "1mm", p: "0mm",boxSizing: "border-box",}} >

              {datar.length > 0 ? (datar.map((item) => ( <QRItem key={item.id} id={item.id} showText={qrIdPrint} /> )) ) : ( 
                  <Typography sx={{ p: 3, color: "#888" }}> Select a draw, enter how many coupons, then Apply. </Typography> )}
                  
          </Box>
            
      </Container>

       {loading && <GradientLoader text="Loading" />} 
    </Box>
  );

}

export default PrintPhysicalCardQr;